"use client";

import { useEffect, useState, Suspense } from "react";
import Link from "next/link";
import { viewItem, search as gaSearch } from "@/helpers/gtag";

import useSWR from "swr";
import Hero from "@/components/homes/home-6/Hero";
import Features from "@/components/common/Features";
import CategoryProducts from "@/components/common/CategoryProducts";
import dynamic from "next/dynamic";

const DynamicDetailsPopup = dynamic(() => import("@/components/shopDetails/DetailsPopup"), { ssr: false });
const DynamicShopDetailsTab = dynamic(() => import("@/components/shopDetails/ShopDetailsTab"), { ssr: false });
const DynamicShopFullwidth = dynamic(() => import("@/components/shop/ShopFullwidth"), { ssr: false });

/**
 * IMPORTANT:
 * - fetcher aruncă eroare pe non-2xx (altfel SWR consideră "data" și ai stări greșite)
 * - pageType se resetează la schimbarea rutei/keyword (elimină flash-ul de "Pagina nu a fost găsită")
 */
const fetcher = async (url) => {
  const res = await fetch(url);

  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }

  if (!res.ok) {
    const err = new Error("Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
};

function LoadingSkeleton() {
  return (
    <div className="container py-5">
      <div className="animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
        <div className="h-32 bg-gray-300 rounded mb-4"></div>
      </div>
    </div>
  );
}

export default function PageContent({ params, searchParams, customCategories = [] }) {
  const { slug } = params || {};
  const searchQuery = searchParams?.q || null;

  const [pageType, setPageType] = useState(null);

  // slug helpers
  const isMultiLevel = Array.isArray(slug) && slug.length > 1;
  const isSingleLevel = Array.isArray(slug) && slug.length === 1;
  const slugString = Array.isArray(slug) ? slug[slug.length - 1] : slug;
  const slugPath = Array.isArray(slug) ? slug.join("/") : slug;

  const reservedPaths = [
    "autentificare", "checkout", "contact", "cos-de-cumparaturi",
    "formular-retur", "inregistrare", "order-confirmation",
    "page-not-found", "placed-order", "politica-cookies",
    "politica-de-confidentialitate", "problem-order",
    "reduceri", "craciun", "termeni-si-conditii", "toate-produsele", "wishlist"
  ];
  const isReserved = reservedPaths.includes(slugPath);

  // ✅ Resetăm pageType imediat când se schimbă ruta sau query (prevenim flash de notfound)
  useEffect(() => {
    setPageType(null);
  }, [slugPath, searchQuery]);

  // Multi-level categorie (ex: /cat/subcat)
  const { data: multiLevelCategoryData, error: multiLevelCategoryError } = useSWR(
    isMultiLevel && !isReserved ? `/api/categories/${slugPath}` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  // Single-level categorie (ex: /cat)
  const {
    data: singleCategoryData,
    error: singleCategoryError,
    isLoading: categoryLoading,
  } = useSWR(
    (isSingleLevel || (!Array.isArray(slug) && slug)) && !isMultiLevel && !isReserved ? `/api/categories/${slugString}` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  // Single-level produs (ex: /produs)
  const {
    data: product,
    error: productError,
    isLoading: productLoading,
  } = useSWR(
    (isSingleLevel || (!Array.isArray(slug) && slug)) && !isMultiLevel && !isReserved ? `/api/products/${slugString}` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  // Homepage
  const { data: categories, error: categoriesError } = useSWR(
    !slug && !searchQuery ? `/api/categories2` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  // Search
  const buildSearchUrl = () => {
    if (!searchQuery) return null;

    const qp = new URLSearchParams({ q: searchQuery });

    if (slug) {
      const slugArray = Array.isArray(slug) ? slug : [slug];
      if (slugArray[0]) qp.append("category", slugArray[0]);
      if (slugArray[1]) qp.append("subcategory", slugArray[1]);
      if (slugArray[2]) qp.append("thirdcategory", slugArray[2]);
    }

    return `/api/search?${qp.toString()}`;
  };

  const { data: searchResults, error: searchError } = useSWR(buildSearchUrl(), fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  // ✅ Determinăm tipul paginii doar când avem suficiente informații (nu notfound prematur)
  useEffect(() => {
    if (searchQuery) return setPageType("search");
    if (!slug) return setPageType("homepage");

    // multi-level: doar categorie
    if (isMultiLevel) {
      if (multiLevelCategoryData) return setPageType("category");
      if (multiLevelCategoryError) return setPageType("notfound");
      return; // încă se încarcă
    }

    // single-level: așteaptă să se termine ambele (categorie + produs)
    if (categoryLoading || productLoading) return;

    // prioritate: categorie
    if (singleCategoryData) return setPageType("category");

    // apoi produs
    if (product) return setPageType("product");

    // notfound doar dacă ambele au dat eroare
    if (singleCategoryError && productError) return setPageType("notfound");
  }, [
    searchQuery,
    slug,
    slugPath,
    isMultiLevel,
    categoryLoading,
    productLoading,
    multiLevelCategoryData,
    multiLevelCategoryError,
    singleCategoryData,
    singleCategoryError,
    product,
    productError,
  ]);

  // GA4: view_item — fires when a product page is resolved
  useEffect(() => {
    if (product && pageType === "product") {
      viewItem(product);
    }
  }, [product, pageType]);

  // GA4: search — fires when search results arrive
  useEffect(() => {
    if (searchResults && pageType === "search") {
      gaSearch(searchResults.query || "");
    }
  }, [searchResults, pageType]);

  // Erori “globale” (homepage/search)
  if (categoriesError || searchError) {
    return (
      <div className="container text-center py-5">
        <h1>Eroare la încărcarea paginii</h1>
        <Link href="/" className="btn btn-primary">
          Înapoi
        </Link>
      </div>
    );
  }

  // Loading până știm ce pagină e
  if (!pageType) return <LoadingSkeleton />;

  // ----- HOMEPAGE -----
  if (pageType === "homepage") {
    if (!categories) return <LoadingSkeleton />;

    // Înlocuim categoriile specifice cu datele custom venite din SQL (din props)
    const displayCategories = Array.isArray(categories) ? categories.map(cat => {
      if (cat.slug === 'articole-copii') {
        return customCategories.find(c => c.slug === "scutere-tricicluri-trotinete") || cat;
      }
      if (cat.slug === 'documente') {
        return customCategories.find(c => c.slug === "motocultoare-motocoase-generator") || cat;
      }
      return cat;
    }) : [];

    const christmas = customCategories.find(c => c.slug === "colectia-de-craciun");

    return (
      <>
        <Hero />
        <Suspense fallback={<LoadingSkeleton />}>
          {christmas?.products?.length > 0 && <CategoryProducts category={christmas} />}
          {displayCategories.map(
            (cat) => cat?.products?.length > 0 && <CategoryProducts key={cat.slug} category={cat} />
          )}
        </Suspense>
        <div className="mt-5"></div>
        <Features />
      </>
    );
  }

  // ----- PRODUS -----
  if (pageType === "product") {
    if (!product) return <LoadingSkeleton />;

    return (
      <>
        <div className="tf-breadcrumb">
          <div className="container">
            <div className="tf-breadcrumb-wrap d-flex justify-content-between flex-wrap align-items-center">
              <div className="tf-breadcrumb-list">
                <Link href={`/`} className="text">
                  Acasă
                </Link>
                <i className="icon icon-arrow-right" />

                {product.categorySlug && (
                  <>
                    <span className="text">
                      <Link href={`/${product.categorySlug}`}>{product.categoryName}</Link>
                    </span>
                  </>
                )}

                {product.subcategorySlug && (
                  <>
                    <i className="icon icon-arrow-right" />
                    <span className="text">
                      <Link href={`/${product.categorySlug}/${product.subcategorySlug}`}>{product.subcategoryName}</Link>
                    </span>
                  </>
                )}

                {product.thirdCategorySlug && (
                  <>
                    <i className="icon icon-arrow-right" />
                    <span className="text">
                      <Link href={`/${product.categorySlug}/${product.subcategorySlug}/${product.thirdCategorySlug}`}>
                        {product.thirdCategoryName}
                      </Link>
                    </span>
                  </>
                )}

                <i className="icon icon-arrow-right" />
                <span className="text">{product.name}</span>
              </div>
            </div>
          </div>
        </div>

        <Suspense fallback={<LoadingSkeleton />}>
          <DynamicDetailsPopup product={product} randomUsers={Math.floor(Math.random() * 20) + 1} />
          <DynamicShopDetailsTab product={product} />
        </Suspense>
      </>
    );
  }

  // ----- CATEGORIE -----
  const activeCategoryData = isMultiLevel ? multiLevelCategoryData : singleCategoryData;

  if (pageType === "category") {
    if (!activeCategoryData) return <LoadingSkeleton />;

    return (
      <>
        <div className="tf-page-title">
          <div className="container-full">
            <div className="heading text-center">
              {activeCategoryData.categoryName}
              {activeCategoryData.subcategoryName && ` > ${activeCategoryData.subcategoryName}`}
              {activeCategoryData.thirdCategoryName && ` > ${activeCategoryData.thirdCategoryName}`}
            </div>
            {activeCategoryData.categoryDesc && (
              <p className="text-center text-2 text_black-2 mt_5">{activeCategoryData.categoryDesc}</p>
            )}
          </div>
        </div>

        {activeCategoryData.productsData && activeCategoryData.productsData.length > 0 ? (
          <Suspense fallback={<LoadingSkeleton />}>
            <DynamicShopFullwidth productsData={activeCategoryData.productsData} />
          </Suspense>
        ) : (
          <div className="container text-center py-5">
            <div className="mb-4">
              <i className="icon icon-bag text-muted" style={{ fontSize: "64px", opacity: 0.3 }}></i>
            </div>
            <h3>Nu am găsit produse în această categorie</h3>
            <p className="text-muted">Reveniți curând sau explorați alte categorii.</p>
            <Link href="/" className="btn btn-primary mt-3">
              Înapoi la homepage
            </Link>
          </div>
        )}
      </>
    );
  }

  // ----- SEARCH -----
  if (pageType === "search") {
    if (!searchResults) return <LoadingSkeleton />;

    const categoryContext = slug ? (Array.isArray(slug) ? slug : [slug]) : [];

    return (
      <>
        <div className="tf-page-title">
          <div className="container-full">
            <div className="heading text-center">
              Rezultate căutare: {searchResults.query}
              {categoryContext.length > 0 && (
                <span className="text-muted"> în {categoryContext.join(" > ")}</span>
              )}
            </div>
            <p className="text-center text-2 text_black-2 mt_5">
              {searchResults.total} {searchResults.total === 1 ? "produs găsit" : "produse găsite"}
            </p>
          </div>
        </div>

        {searchResults.products && searchResults.products.length > 0 ? (
          <Suspense fallback={<LoadingSkeleton />}>
            <DynamicShopFullwidth productsData={searchResults.products} />
          </Suspense>
        ) : (
          <div className="container text-center py-5">
            <h3>Nu am găsit produse pentru {searchResults.query}</h3>
            <Link href="/" className="btn btn-primary mt-3">
              Înapoi la homepage
            </Link>
          </div>
        )}
      </>
    );
  }

  // ----- NOT FOUND -----
  if (pageType === "notfound") {
    return (
      <div className="container text-center py-5">
        <h1>Pagina nu a fost găsită</h1>
        <Link href="/" className="btn btn-primary">
          Înapoi
        </Link>
      </div>
    );
  }

  // fallback sigur (nu afișa notfound “default”)
  return <LoadingSkeleton />;
}
